import { CircleHelp } from "lucide-react";
import React, { useState } from "react";
import { regexes } from "zod/v4";

import {
  IgButton,
  IgChip,
  IgTextField,
  IgTooltip,
  IgTypography,
} from "@intelligems/ig-theme";
import { ConfigurationDto } from "@intelligems/ig-types";
import { Grid, IconButton } from "@mui/material";

import type { ConfigurationInputProps } from "../../../../../types/organization";
import { toProperCaseWithSpaces } from "../../../../../utils";

export const ListConfigInput = ({
  config,
  onChange,
  tooltipMessage,
  title,
}: ConfigurationInputProps) => {
  const [text, setText] = useState<string>("");
  const { error, validate } = useValidation(config);

  const handlePriceClassUnselect = (index: number) => {
    const selectedClasses = config.value.filter(
      (selected: any) => config.value.indexOf(selected) !== index
    );
    onChange(selectedClasses);
  };

  return (
    <Grid container direction="column">
      {/* Row: label, input, and add button */}
      <Grid item>
        <Grid container spacing={2} sx={{ width: "100%", mx: "auto" }}>
          {" "}
          <Grid item sx={{ minWidth: "350px" }}>
            <IgTypography>
              {title || toProperCaseWithSpaces(config.key)}
            </IgTypography>
          </Grid>
          {tooltipMessage && (
            <Grid item>
              <IgTooltip title={tooltipMessage}>
                <IconButton size="small" aria-label="info">
                  <CircleHelp size={16} />
                </IconButton>
              </IgTooltip>
            </Grid>
          )}
          <Grid item xs>
            <IgTextField
              error={error !== null}
              fullWidth
              helperText={error || " "}
              value={text}
              onChange={(e: any) => setText(e.target.value)}
              id="outlined-basic"
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item>
            <IgButton
              style={{ textTransform: "none" }}
              size="medium"
              variant="outlined"
              onClick={() => {
                if (validate(text)) {
                  onChange([...config.value, text]);
                  setText("");
                }
              }}
            >
              Add
            </IgButton>
          </Grid>
        </Grid>
      </Grid>

      {/* Row: selected chips beneath input */}
      {config.value.length > 0 && (
        <Grid item>
          <Grid container direction="row" spacing={1}>
            {config.value.map((value: string, index: number) => (
              <Grid item key={index}>
                <IgChip
                  onDelete={() => handlePriceClassUnselect(index)}
                  label={value}
                  key={value}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

const useValidation = (
  config: ConfigurationDto
): {
  error: string | null;
  validate: (v: string) => boolean;
} => {
  const [error, setError] = useState<string | null>(null);

  let validate: (v: string) => boolean = () => true;
  switch (config.key) {
    case "exclude_ip_addresses":
      validate = (v: string): boolean => {
        if (!regexes.ipv4.test(v)) {
          setError("Invalid IP address");
          return false;
        } else {
          setError(null);
          return true;
        }
      };
      break;
    case "add_ig_discount_to_href_query_selectors":
    case "additional_cart_discount_message_selectors":
    case "additional_cart_original_total_selectors":
    case "additional_cart_savings_selectors":
    case "additional_compare_price_query_selectors":
    case "additional_installment_price_query_selectors":
    case "additional_price_query_selectors":
    case "additional_savings_percentage_query_selectors":
    case "additional_savings_price_query_selectors":
    case "additional_volume_discount_query_selectors":
    case "shadow_element_selectors":
      validate = (v: string): boolean => {
        try {
          document.querySelector(v);
          setError(null);
          return true;
        } catch (e) {
          setError("Invalid selector");
          return false;
        }
      };
      break;
    default:
      break;
  }

  return { error, validate };
};
